/**
 * Machinery Simple Direct Guidance - No Mappings, No Complexity
 * Every requirement ID maps directly to its guidance
 * Based on Regulations Assistant verified guidance from MACHINERY_EQUIPMENT_GUIDANCE_PART1.md and PART2.md
 */

export const MACHINERY_SIMPLE_GUIDANCE = {
  
  // Main propulsion and engine systems
  
  'machinery.propulsion.main': {
    id: 'machinery.propulsion.main',
    name: 'Main Propulsion System',
    equipment_type: 'propulsion_system',
    
    placement: {
      requirements: [
        'Installed per marine engineering standards',
        'Suitable for intended area category of operation (WBC3 8.1.1)',
        'Accessible for maintenance and inspection',
        'Protected from weather and sea conditions'
      ]
    },
    
    specifications: {
      type: 'Propulsion system suitable for marine use',
      standards: 'Marine use propulsion per WBC3 8.1.1',
      trigger: 'Universal requirement for all seagoing vessels',
      suitability: 'Must be suitable for marine environment and intended operations',
      fuel_capacity: 'Sufficient fuel capacity or charge for area category (WBC3 8.1.1)',
      options: ['Diesel engine', 'Petrol engine', 'Electric motor', 'Hybrid system'],
      marine_use: 'Engine/motor must be designed and certified for marine application'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent engineer',
        'Regular servicing per manufacturer recommendations',
        'Verify fuel capacity adequate for area category',
        'Check propulsion system suitability for marine use'
      ],
      recommended: [
        'Pre-voyage propulsion system checks',
        'Monitor engine performance and fuel consumption',
        'Regular inspection of propulsion system mountings',
        'Verify continued suitability for operational area'
      ]
    },
    
    authority: 'WBC3 8.1.1'
  },

  'machinery.steering.system': {
    id: 'machinery.steering.system',
    name: 'Steering System',
    equipment_type: 'steering_system',
    
    placement: {
      requirements: [
        'Installed for effective vessel control',
        'Accessible from primary steering position',
        'Protected from damage and weather',
        'Connected to rudder or propulsion steering mechanism'
      ]
    },
    
    specifications: {
      type: 'Steering system for vessel directional control',
      standards: 'Steering system per WBC3 10.2.1',
      trigger: 'Universal requirement for all seagoing vessels',
      control_options: [
        'Direct steering control',
        'Remote steering control'
      ],
      effectiveness: 'Must provide effective vessel directional control',
      redundancy: 'Consider backup steering capability for safety'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of steering system',
        'Verify steering response and effectiveness',
        'Check steering system connections and linkages',
        'Test from primary steering position'
      ],
      recommended: [
        'Pre-voyage steering system function check',
        'Regular lubrication of steering mechanisms',
        'Monitor steering system wear and backlash',
        'Test emergency steering procedures if applicable'
      ]
    },
    
    authority: 'WBC3 10.2.1'
  },

  'machinery.fuel.tanks': {
    id: 'machinery.fuel.tanks',
    name: 'Fuel Tanks',
    equipment_type: 'fuel_storage',
    
    placement: {
      requirements: [
        'Positioned for safe fuel storage and access',
        'Secured against vessel motion and impact',
        'Ventilated per WBC3 requirements',
        'Located away from ignition sources and heat'
      ]
    },
    
    specifications: {
      type: 'Fuel storage tanks for propulsion system',
      standards: 'Fuel tank requirements per WBC3 8.12',
      trigger: 'Required for all seagoing vessels with fuel-powered propulsion',
      capacity: 'Sufficient for intended area category of operation (WBC3 8.1.1)',
      installation: 'Permanent or portable tanks per vessel size and configuration',
      materials: 'Suitable materials for fuel storage and marine environment',
      ventilation: 'Proper ventilation to prevent gas accumulation'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of tank condition and integrity',
        'Verify fuel capacity adequate for operations',
        'Check tank ventilation and fittings',
        'Inspect tank mountings and security'
      ],
      recommended: [
        'Regular fuel quality checks and treatment',
        'Monitor tank for corrosion or damage',
        'Check fuel lines and connections',
        'Maintain fuel level indicators and gauges'
      ]
    },
    
    authority: 'WBC3 8.12, 8.1.1'
  },

  'machinery.killcord': {
    id: 'machinery.killcord',
    name: 'Kill Cord System',
    equipment_type: 'engine_safety',
    
    placement: {
      requirements: [
        'Fitted at open steering position (WBC3 8.9.1)',
        'Readily accessible to vessel operator',
        'Connected to engine stop mechanism',
        'Protected from accidental activation but immediately available'
      ]
    },
    
    specifications: {
      type: 'Kill cord engine safety system',
      standards: 'Kill cord per WBC3 8.9.1',
      trigger: 'Outboard engines with open steering positions',
      function: 'Stops engine when operator leaves steering position',
      attachment: 'Securely attached to operator with lanyard or clip',
      activation: 'Immediate engine shutdown when cord is pulled',
      reliability: 'Must function reliably in marine environment'
    },
    
    maintenance: {
      mandatory: [
        'Annual testing of kill cord function',
        'Verify immediate engine shutdown when activated',
        'Check kill cord attachment points and mechanism',
        'Test from normal operating positions'
      ],
      recommended: [
        'Pre-voyage kill cord function test',
        'Check kill cord lanyard condition regularly',
        'Ensure operator familiarity with kill cord use',
        'Verify kill cord mechanism free from corrosion'
      ]
    },
    
    authority: 'WBC3 8.9.1'
  },

  'machinery.bilge.pump.hand': {
    id: 'machinery.bilge.pump.hand',
    name: 'Hand Bilge Pump',
    equipment_type: 'bilge_pump',
    
    placement: {
      requirements: [
        'Fitted in each separate space (WBC3 11.2.1.1)',
        'Accessible for manual operation',
        'Connected to bilge suction system',
        'Positioned above normal waterline for operation'
      ]
    },
    
    specifications: {
      type: 'Manual hand-operated bilge pump',
      standards: 'Hand bilge pump per WBC3 11.2.1.1',
      trigger: 'Each separate space on all seagoing vessels',
      quantity: 'At least one hand pump per separate space',
      operation: 'Manual operation independent of power systems',
      capacity: 'Adequate pumping capacity for space size',
      reliability: 'Must function when power systems fail'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of pump operation',
        'Verify pump capacity and discharge capability',
        'Check pump mounting and accessibility',
        'Test suction and discharge lines'
      ],
      recommended: [
        'Monthly pump operation test',
        'Check pump handle and mechanism condition',
        'Verify suction strainer clear and functional',
        'Lubricate pump mechanism as required'
      ]
    },
    
    authority: 'WBC3 11.2.1.1'
  },

  'machinery.bilge.pump.powered': {
    id: 'machinery.bilge.pump.powered',
    name: 'Powered Bilge Pump',
    equipment_type: 'bilge_pump',
    
    placement: {
      requirements: [
        'Fitted in each separate space (WBC3 11.2.1.1)',
        'Connected to power source (engine or independent)',
        'Accessible for maintenance and operation',
        'Connected to bilge suction and discharge system'
      ]
    },
    
    specifications: {
      type: 'Engine driven or independently powered bilge pump',
      standards: 'Powered bilge pump per WBC3 11.2.1.1',
      trigger: 'Each separate space on all seagoing vessels',
      power_options: [
        'Engine driven pump',
        'Independently powered pump (electric/hydraulic)'
      ],
      capacity: 'Adequate pumping capacity for space size and flood rate',
      reliability: 'Must provide reliable bilge water removal',
      automation: 'May include automatic operation capability'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of powered pump',
        'Verify pump capacity and power supply',
        'Check pump operation and discharge capability',
        'Test automatic operation if fitted'
      ],
      recommended: [
        'Monthly powered pump operation test',
        'Check pump impeller and housing condition',
        'Verify power supply connections and operation',
        'Monitor pump discharge rate and pressure'
      ]
    },
    
    authority: 'WBC3 11.2.1.1'
  },

  'machinery.bilge.alarm': {
    id: 'machinery.bilge.alarm',
    name: 'Bilge Alarm',
    equipment_type: 'bilge_alarm',
    
    placement: {
      requirements: [
        'Fitted in watertight compartments containing machinery (WBC3 11.3.1)',
        'Located to detect water accumulation effectively',
        'Alarm audible at control positions',
        'Protected from normal bilge water splash'
      ]
    },
    
    specifications: {
      type: 'Bilge water level alarm system',
      standards: 'Bilge alarm per WBC3 11.3.1',
      trigger: 'Watertight compartments containing machinery',
      detection: 'Automatic detection of abnormal water accumulation',
      alarm_location: 'Audible and/or visual alarm at control position',
      sensitivity: 'Set to activate before water reaches critical levels',
      power: 'Connected to vessel electrical system or independent power'
    },
    
    maintenance: {
      mandatory: [
        'Annual testing of bilge alarm function',
        'Verify alarm activation at correct water level',
        'Check alarm audibility and visibility at control positions',
        'Test alarm sensor and wiring integrity'
      ],
      recommended: [
        'Monthly bilge alarm functional test',
        'Check alarm sensor for fouling or damage',
        'Verify alarm reset and acknowledgment functions',
        'Test during normal vessel operations for audibility'
      ]
    },
    
    authority: 'WBC3 11.3.1'
  },

  'machinery.rudder.system': {
    id: 'machinery.rudder.system',
    name: 'Rudder System',
    equipment_type: 'rudder_system',
    
    placement: {
      requirements: [
        'Designed per Administration-recognised standard (WBC3 10.3.1)',
        'Properly supported by rudder stock and bearings',
        'Connected to steering system effectively',
        'Protected from damage during vessel operations'
      ]
    },
    
    specifications: {
      type: 'Complete rudder system including blade, stock, and fittings',
      standards: 'Standard recognised by Administration per WBC3 10.3.1',
      trigger: 'Where rudder is fitted as steering mechanism',
      components: [
        'Rudder blade',
        'Rudder stock',
        'Rudder bearings and supports',
        'Connection to steering system'
      ],
      design: 'Design and construction per recognised standard',
      effectiveness: 'Must provide effective steering control'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of rudder system components',
        'Check rudder blade condition and attachment',
        'Verify rudder stock and bearing condition',
        'Test steering system connection and response'
      ],
      recommended: [
        'Regular visual inspection of rudder during haul-out',
        'Check rudder movement for binding or excessive play',
        'Lubricate rudder bearings per manufacturer schedule',
        'Monitor rudder system alignment and wear'
      ]
    },
    
    authority: 'WBC3 10.3.1'
  },

  'machinery.engine.starting': {
    id: 'machinery.engine.starting',
    name: 'Engine Starting System',
    equipment_type: 'engine_starting',
    
    placement: {
      requirements: [
        'Installed per engine manufacturer requirements',
        'Accessible for operation and maintenance',
        'Protected from weather and marine environment',
        'Connected to engine starting mechanism'
      ]
    },
    
    specifications: {
      type: 'Engine starting system',
      standards: 'Engine starting per WBC3 8.8.1',
      trigger: 'All seagoing vessels with engines requiring starting system',
      starting_methods: [
        'Mechanical means (recoil, hand crank)',
        'Compressed air starting',
        'Battery electric starting'
      ],
      reliability: 'Must provide reliable engine starting capability',
      backup: 'Consider backup starting method for redundancy',
      suitability: 'Appropriate for engine type and vessel operations'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of starting system',
        'Verify reliable engine starting under normal conditions',
        'Check starting system components and connections',
        'Service per engine manufacturer recommendations'
      ],
      recommended: [
        'Pre-voyage starting system function test',
        'Regular exercise of starting system',
        'Monitor starting system performance and reliability',
        'Maintain backup starting capability if fitted'
      ]
    },
    
    authority: 'WBC3 8.8.1'
  },

  'machinery.battery.backup': {
    id: 'machinery.battery.backup',
    name: 'Backup Starting Battery',
    equipment_type: 'backup_battery',
    
    placement: {
      requirements: [
        'Located for engine starting capability (WBC3 8.8.2)',
        'Secured against vessel motion',
        'Ventilated per battery type requirements',
        'Accessible for maintenance and testing'
      ]
    },
    
    specifications: {
      type: 'Backup battery for engine starting',
      standards: 'Backup battery per WBC3 8.8.2',
      trigger: 'Where battery is sole means of engine starting',
      capacity: 'Adequate capacity for engine starting requirements',
      charging: 'Charging facility required (WBC3 8.8.2)',
      independence: 'Separate from main electrical system battery',
      reliability: 'Must provide reliable backup starting capability'
    },
    
    maintenance: {
      mandatory: [
        'Annual battery inspection and capacity testing',
        'Verify charging facility operation',
        'Check battery condition and electrolyte levels',
        'Test backup starting capability under load'
      ],
      recommended: [
        'Monthly battery condition and charge level check',
        'Clean battery terminals and connections',
        'Monitor charging system performance',
        'Replace battery per manufacturer service life recommendations'
      ]
    },
    
    authority: 'WBC3 8.8.2'
  },

  // Additional machinery systems from PART 2
  
  'machinery.air.receivers': {
    id: 'machinery.air.receivers',
    name: 'Air Start Receivers',
    equipment_type: 'air_receivers',
    
    placement: {
      requirements: [
        'Two air receivers required for air start systems (WBC3 8.8.3)',
        'Located for effective air distribution to engine starting system',
        'Protected from damage and weather',
        'Accessible for maintenance, inspection, and testing'
      ]
    },
    
    specifications: {
      type: 'Two air receivers for compressed air engine starting',
      standards: 'Recognised Organisation standard per WBC3 8.8.3',
      trigger: 'Air start engine systems',
      capacity: 'Each receiver with sufficient capacity for 6 consecutive cold starts',
      quantity: '2 air receivers minimum (WBC3 8.8.3)',
      design: 'Design, maintenance, inspection, and testing per RO standard',
      independence: 'Each receiver independently capable of 6 consecutive starts',
      pressure_system: 'Compressed air system with appropriate pressure relief'
    },
    
    maintenance: {
      mandatory: [
        'Annual service per Recognised Organisation standards (WBC3 8.8.3)',
        'Pressure testing per RO requirements',
        'Verify 6 consecutive start capacity for each receiver',
        'Inspection of pressure relief valves and safety systems'
      ],
      recommended: [
        'Monthly air system pressure checks',
        'Regular drain of moisture from receivers',
        'Check air compressor operation and capacity',
        'Monitor receiver pressure gauges and safety devices'
      ]
    },
    
    authority: 'WBC3 8.8.3'
  },

  'machinery.ventilation.machinery': {
    id: 'machinery.ventilation.machinery',
    name: 'Machinery Space Ventilation',
    equipment_type: 'machinery_ventilation',
    
    placement: {
      requirements: [
        'Installed in machinery spaces for adequate ventilation',
        'Stop controls located outside machinery space (WBC3 15.1.1.4)',
        'Remote stop capability in event of fire',
        'Positioned for effective air circulation and cooling'
      ]
    },
    
    specifications: {
      type: 'Machinery space ventilation fans with remote stop capability',
      standards: 'Fire control ventilation per WBC3 15.1.1.4',
      trigger: 'Machinery spaces requiring ventilation',
      control_types: [
        'Fans within machinery space(s) - stoppable from outside',
        'Fans feeding machinery space(s) - stoppable remotely'
      ],
      fire_safety: 'Must be capable of being stopped in event of fire',
      stop_location: 'Stop controls positioned outside or remote to machinery space',
      ventilation: 'Adequate ventilation for machinery cooling and air quality'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of ventilation fan operation',
        'Test remote stop capability from outside machinery space',
        'Verify stop controls accessible during fire scenarios',
        'Check fan motor and electrical connections'
      ],
      recommended: [
        'Monthly ventilation fan operation test',
        'Clean fan blades and housings regularly',
        'Check ventilation ducting for blockages',
        'Monitor fan performance and air flow rates'
      ]
    },
    
    authority: 'WBC3 15.1.1.4'
  },

  'machinery.fuel.isolation': {
    id: 'machinery.fuel.isolation',
    name: 'Fuel Isolation Valve',
    equipment_type: 'fuel_isolation',
    
    placement: {
      requirements: [
        'Positioned outside the machinery space (WBC3 8.10.3)',
        'Fitted as close to fuel tank as possible (WBC3 8.10.3)',
        'Accessible for emergency operation during fire',
        'Protected from damage but readily operable'
      ]
    },
    
    specifications: {
      type: 'Fuel isolation valve or cock for machinery space fire protection',
      standards: 'Fuel isolation per WBC3 8.10.3',
      trigger: 'Machinery spaces with fuel systems that may feed fires',
      location: 'Outside machinery space, close to fuel tank',
      operation_options: [
        'Manual valve or cock',
        'Remotely operated with manual override (WBC3 8.10.3)'
      ],
      manual_override: 'Remote valves must have manual override capability',
      purpose: 'Isolate fuel source that may feed machinery space fire'
    },
    
    maintenance: {
      mandatory: [
        '6-monthly operation test of isolation valve (established practice)',
        'Verify manual override operation if remotely operated',
        'Check valve accessibility from outside machinery space',
        'Test valve closure and fuel flow stoppage'
      ],
      recommended: [
        'Monthly visual inspection of valve condition',
        'Check valve labeling and identification',
        'Verify crew familiarity with valve location and operation',
        'Lubricate valve mechanism as required'
      ]
    },
    
    authority: 'WBC3 8.10.3'
  }
  
  // TODO: No clear source text found for the following IDs that may exist in the audit:
  // machinery.engine.cooling, machinery.transmission, machinery.drive.system
  // These were not found in the provided RA markdown guidance files
};

/**
 * Simple Direct Lookup - No mappings, no complexity
 * @param {string} itemId - Equipment requirement ID
 * @returns {Object|null} Guidance object or null
 */
export function getMachinerySimpleGuidance(itemId) {
  return MACHINERY_SIMPLE_GUIDANCE[itemId] || null;
}